"use client";

import React, { useState, useEffect } from "react";
import { X, Minimize2, Maximize2 } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { useSession } from "next-auth/react";
import { useChatContext } from "stream-chat-react";
import {
  Channel,
  Window,
  ChannelHeader,
  MessageList,
  MessageInput,
  Thread,
} from "stream-chat-react";
import { Chat } from "stream-chat-react";
import "../../../node_modules/stream-chat-react/dist/scss/v2/index.scss";
import "../streamChat/style.scss";
import "./globalChatPopup.css";

interface GlobalChatPopupProps {
  // This component will be controlled by custom events
}

const GlobalChatPopup: React.FC<GlobalChatPopupProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [channel, setChannel] = useState<any>(null);
  const [resolvedRecipientName, setResolvedRecipientName] = useState<string>("");
  const [currentChannelId, setCurrentChannelId] = useState<string | null>(null);
  const [processedOpenEvents, setProcessedOpenEvents] = useState<Set<string>>(new Set());

  const session = useSession();
  const { client } = useChatContext();

  // Function to resolve recipient name from channel ID or entity ID
  const resolveRecipientName = async (name: string, channelId?: string) => {
    if (name && (name === "Admin" || name === "admin" || name.includes("_"))) {
      const parts = name.split("_");
      let entityId;
      if (parts.length >= 2) {
        entityId = parts[1];
      } else {
        entityId = parts[0];
      }

      const response = await fetch(`/next/api/user/name?entityId=${entityId}`);
      if (response.ok) {
        const userData = await response.json();
        if (userData && userData.name) {
          return userData.name;
        }
      }
    }
    return name;
  };

  // Listen for global chat open events
  useEffect(() => {
    const handleGlobalChatOpen = async (event: CustomEvent) => {
      const { channelId, senderName } = event.detail;

      if (channelId && senderName) {
        const eventId = `${channelId}_${senderName}_${Date.now()}`;

        if (processedOpenEvents.has(eventId)) {
          return;
        }

        setProcessedOpenEvents((prev) => new Set(prev).add(eventId));

        const resolvedName = await resolveRecipientName(senderName, channelId);
        setResolvedRecipientName(resolvedName);
        setCurrentChannelId(channelId);
        setIsOpen(true);
        setIsMinimized(false);
      }
    };

    window.addEventListener("open-chat-popup", handleGlobalChatOpen as unknown as EventListener);

    return () => {
      window.removeEventListener(
        "open-chat-popup",
        handleGlobalChatOpen as unknown as EventListener
      );
    };
  }, [processedOpenEvents]);

  // Handle channel connection when chat opens and client is available
  useEffect(() => {
    if (isOpen && client && currentChannelId && client.user) {
      let cleanChannelId = currentChannelId;
      if (currentChannelId.includes(":")) {
        cleanChannelId = currentChannelId.split(":")[1];
      }

      try {
        const existingChannel = client.channel("messaging", cleanChannelId);
        setChannel(existingChannel);

        existingChannel
          .watch()
          .then(() => {
            // Ensure this channel is added to subscribed channels in GetStreamProvider
            if ((window as any).createOrGetChannel && resolvedRecipientName) {
              const channelIdParts = cleanChannelId.split("_");
              if (channelIdParts.length >= 3) {
                const recipientId = channelIdParts[2];
                (window as any)
                  .createOrGetChannel(recipientId, resolvedRecipientName)
                  .catch((error: any) => {
                    // Error ensuring channel subscription
                  });
              }
            }
          })
          .catch((error) => {
            // Error watching channel
          });

        // Dispatch chat opened event
        const chatOpenedEvent = new CustomEvent("chat-popup-opened", {
          detail: { channelId: cleanChannelId },
        });
        window.dispatchEvent(chatOpenedEvent);
      } catch (error) {
        // Error creating channel
      }
    }
  }, [isOpen, client, currentChannelId]);

  // Wrapper function to handle chat close with event dispatch
  const handleClose = () => {
    const chatClosedEvent = new CustomEvent("chat-popup-closed", {
      detail: { channelId: currentChannelId },
    });
    window.dispatchEvent(chatClosedEvent);

    setIsOpen(false);
    setCurrentChannelId(null);
    setChannel(null);
    setResolvedRecipientName("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat Popup Container */}
      <div
        className={`bg-white rounded-lg shadow-2xl border border-gray-200 transition-all duration-300 ${
          isMinimized ? "w-80 h-12" : "w-96 h-[500px]"
        }`}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-3 bg-blue-600 text-white rounded-t-lg cursor-pointer"
          onClick={() => setIsMinimized(!isMinimized)}
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="font-medium text-sm">
              {isMinimized
                ? `Chat with ${resolvedRecipientName || "User"}`
                : `Chat with ${resolvedRecipientName || "User"}`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-white hover:bg-blue-700"
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
            >
              {isMinimized ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-white hover:bg-blue-700"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Enhanced Chat Content with Stream Chat Components */}
        {!isMinimized && (
          <div className="h-[calc(500px-48px)]">
            {(() => {
              if (channel && client?.user) {
                return (
                  <div className="h-full popup-chat-container">
                    <Chat client={client} theme="str-chat__theme-light">
                      <Channel channel={channel}>
                        <Window>
                          <ChannelHeader />
                          <MessageList />
                          <MessageInput focus={true} />
                        </Window>
                        <Thread />
                      </Channel>
                    </Chat>
                  </div>
                );
              } else {
                return (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-gray-500">
                      {!client ? "Connecting to chat..." : "Loading chat..."}
                    </div>
                  </div>
                );
              }
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalChatPopup;
